import React from "react";
import {
  TrendingDown, Loader2, ChevronLeft, Calendar
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer
} from "recharts";
import { Flag, Target, Edit2, Trash2, Plus } from "lucide-react";

const MS_STATUS_CFG = {
  pending:     { label: "Pendiente",   cls: "bg-gray-100 text-gray-600",   dot: "bg-gray-400" },
  in_progress: { label: "En progreso", cls: "bg-amber-100 text-amber-700", dot: "bg-amber-400" },
  completed:   { label: "Completado",  cls: "bg-green-100 text-green-700", dot: "bg-green-500" },
  delayed:     { label: "Retrasado",   cls: "bg-red-100 text-red-700",     dot: "bg-red-500" },
};

/**
 * Burndown chart panel for a selected sprint.
 * Props: burndownSprint, burndownData, burndownLoading, onClose
 */
export function ProjectBurndownPanel({ burndownSprint, burndownData, burndownLoading, onClose }) {
  if (!burndownSprint) return null;
  return (
    <div className="border-t border-gray-100 p-5" data-testid="burndown-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
            <ChevronLeft size={16} />
          </button>
          <div>
            <h3 className="text-base font-bold text-gray-900 font-['Outfit'] flex items-center gap-2">
              <TrendingDown size={16} className="text-[#007934]" />
              Burndown — Sprint {burndownSprint.number}: {burndownSprint.name}
            </h3>
            <p className="text-xs text-gray-500">{burndownSprint.start_date} → {burndownSprint.end_date}</p>
          </div>
        </div>
      </div>

      {burndownLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={24} className="animate-spin text-[#007934]" />
        </div>
      ) : burndownData ? (
        <>
          {burndownData.velocity && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
              {[
                { label: "Total items", val: burndownData.velocity.total_items, cls: "text-gray-900" },
                { label: "Completados", val: burndownData.velocity.completed, cls: "text-[#007934]" },
                { label: "Restantes", val: burndownData.velocity.remaining, cls: burndownData.velocity.remaining > 0 ? "text-amber-600" : "text-[#007934]" },
                { label: "Velocidad", val: `${burndownData.velocity.completion_pct}%`, cls: "text-blue-600" },
              ].map(k => (
                <div key={k.label} className="bg-gray-50 rounded-xl px-4 py-3 text-center border border-gray-100">
                  <p className={`text-2xl font-bold font-['Outfit'] ${k.cls}`}>{k.val}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{k.label}</p>
                </div>
              ))}
            </div>
          )}

          {burndownData.daily_data?.length > 0 ? (
            <div className="bg-white rounded-xl border border-gray-100 p-4" data-testid="burndown-chart">
              <div className="flex items-center gap-4 mb-3 text-xs">
                <div className="flex items-center gap-1.5"><div className="w-6 h-0.5 bg-gray-300" style={{ borderTop: "2px dashed #9ca3af" }} /><span className="text-gray-500">Ideal</span></div>
                <div className="flex items-center gap-1.5"><div className="w-6 h-0.5 bg-[#007934]" /><span className="text-gray-500">Real</span></div>
              </div>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={burndownData.daily_data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#9ca3af" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ borderRadius: "8px", border: "1px solid #e5e7eb", fontSize: "12px" }}
                    formatter={(value, name) => [value, name === "ideal" ? "Ideal" : "Real"]}
                    labelFormatter={l => `Día: ${l}`}
                  />
                  <Line type="monotone" dataKey="ideal" stroke="#9ca3af" strokeWidth={1.5} strokeDasharray="5 5" dot={false} name="ideal" />
                  <Line type="monotone" dataKey="actual" stroke="#007934" strokeWidth={2.5} dot={{ r: 3, fill: "#007934" }} activeDot={{ r: 5 }} connectNulls={false} name="actual" />
                </LineChart>
              </ResponsiveContainer>
              <p className="text-xs text-gray-400 text-center mt-2">Items restantes por día · Línea ideal vs. progreso real</p>
            </div>
          ) : (
            <div className="text-center py-10 text-gray-400 bg-gray-50 rounded-xl">
              <TrendingDown size={32} className="mx-auto mb-2 opacity-20" />
              <p className="text-sm">Sin datos de burndown</p>
              <p className="text-xs mt-1">Asigna work items a este sprint para ver el gráfico</p>
            </div>
          )}

          {burndownData.velocity?.total_story_points > 0 && (
            <div className="mt-4 bg-blue-50 rounded-xl px-4 py-3 border border-blue-100">
              <p className="text-xs font-semibold text-blue-700 mb-1">Story Points</p>
              <div className="flex items-center gap-4 text-sm">
                <span className="text-gray-700">Total: <b>{burndownData.velocity.total_story_points}</b></span>
                <span className="text-[#007934]">Completados: <b>{burndownData.velocity.completed_story_points}</b></span>
                <span className="text-gray-500">Restantes: <b>{burndownData.velocity.total_story_points - burndownData.velocity.completed_story_points}</b></span>
              </div>
              <div className="h-2 bg-blue-200 rounded-full mt-2">
                <div className="h-full bg-blue-500 rounded-full" style={{ width: `${Math.round(burndownData.velocity.completed_story_points / burndownData.velocity.total_story_points * 100)}%` }} />
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

/**
 * Milestones tab panel for a project.
 * Props: milestones, saving, onAdd, onEdit, onDelete, onUpdateStatus
 */
export function ProjectMilestones({ milestones, saving, onAdd, onEdit, onDelete, onUpdateStatus }) {
  return (
    <div className="p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-gray-900 font-['Outfit']">Hitos del Proyecto</h2>
        <button onClick={onAdd}
          className="flex items-center gap-2 bg-[#007934] text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-[#006528]"
          data-testid="add-milestone-btn">
          <Plus size={14} /> Nuevo Hito
        </button>
      </div>

      {milestones.length === 0 ? (
        <div className="text-center py-14 text-gray-400">
          <Target size={36} className="mx-auto mb-3 opacity-20" />
          <p className="text-sm font-medium">Sin hitos definidos</p>
          <p className="text-xs mt-1">Añade los hitos clave del proyecto para hacer seguimiento de fechas importantes</p>
        </div>
      ) : (
        <div className="relative">
          <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-100" />
          <div className="space-y-3">
            {milestones.map(ms => {
              const isOverdue = ms.status !== "completed" && ms.due_date && new Date(ms.due_date) < new Date();
              const statusKey = isOverdue && ms.status !== "completed" ? "delayed" : ms.status;
              const cfg = MS_STATUS_CFG[statusKey] || MS_STATUS_CFG.pending;
              return (
                <div key={ms.id} className="flex gap-4 pl-2 group" data-testid={`milestone-${ms.id}`}>
                  <div className={`w-6 h-6 rounded-full border-2 border-white flex items-center justify-center flex-shrink-0 z-10 mt-2 ${cfg.dot} shadow-sm`}>
                    <Flag size={10} className="text-white" />
                  </div>
                  <div className="flex-1 bg-white rounded-xl border border-gray-100 p-4 hover:border-gray-200 transition-all">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-sm font-semibold text-gray-900">{ms.title}</h3>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${cfg.cls}`}>{cfg.label}</span>
                        </div>
                        {ms.description && <p className="text-xs text-gray-500 mt-1">{ms.description}</p>}
                        <p className="text-xs text-gray-400 mt-1.5 flex items-center gap-1">
                          <Calendar size={10} />
                          {ms.due_date ? new Date(ms.due_date).toLocaleDateString("es-ES", { weekday: "long", day: "2-digit", month: "long", year: "numeric" }) : "Sin fecha"}
                          {isOverdue && ms.status !== "completed" && <span className="text-red-500 font-medium ml-1">· Retrasado</span>}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <select value={ms.status} onChange={e => onUpdateStatus(ms.id, e.target.value)}
                          className="text-xs border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:border-[#007934]"
                          data-testid={`ms-status-${ms.id}`}>
                          <option value="pending">Pendiente</option>
                          <option value="in_progress">En progreso</option>
                          <option value="completed">Completado</option>
                          <option value="delayed">Retrasado</option>
                        </select>
                        <button onClick={() => onEdit(ms)} className="p-1.5 text-gray-300 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-all" data-testid={`edit-ms-${ms.id}`}><Edit2 size={13} /></button>
                        <button onClick={() => onDelete(ms.id)} className="p-1.5 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all" data-testid={`delete-ms-${ms.id}`}><Trash2 size={13} /></button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
